
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { Mail, User, Phone, ArrowLeft, Send, FileText, DollarSign, MapPin, Calendar, AlertCircle, ChevronsRight, Building } from 'lucide-react';

const RequestQuote: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    clientName: '',
    contactPhone: '',
    interventionType: '',
    irveType: '',
    materialOrService: '',
    priority: 'Normale',
    requestTitle: '',
    detailedDescription: '',
    estimatedBudget: '',
    interventionAddress: '',
    city: '',
    postalCode: '',
    preferredDate: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const {
      clientName,
      contactPhone,
      interventionType,
      irveType,
      materialOrService,
      priority,
      requestTitle,
      detailedDescription,
      estimatedBudget,
      interventionAddress,
      city,
      postalCode,
      preferredDate,
    } = formData;

    const subject = `Nouvelle demande de devis: ${requestTitle}`;
    const body = `
      Nouvelle demande de devis
      =======================

      Informations Client
      -------------------
      Client: ${clientName}
      Téléphone de contact: ${contactPhone}

      Type d'Intervention
      --------------------
      Type d'intervention: ${interventionType}
      Type IRVE: ${irveType}
      Choix de matériel ou service: ${materialOrService}
      Priorité: ${priority}

      Détails de la demande
      ---------------------
      Titre de la demande: ${requestTitle}
      Description détaillée: ${detailedDescription}
      Budget estimé: ${estimatedBudget} €

      Lieu et Planning
      ----------------
      Adresse d'intervention: ${interventionAddress}
      Ville: ${city}
      Code Postal: ${postalCode}
      Date préférée: ${preferredDate}
    `;

    window.location.href = `mailto:contact@Elecborne.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto space-y-8 my-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <FileText className="w-8 h-8 mr-3 text-primary-600" />
                Nouvelle Demande de Devis
              </h1>
              <p className="text-gray-600 mt-2">
                Faites votre demande de devis en ligne.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <User className="w-5 h-5 mr-2 text-primary-600" />
              Informations Client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Client * (Nom et Prénom)</label>
                <input type="text" name="clientName" value={formData.clientName} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone de contact *</label>
                <input type="tel" name="contactPhone" value={formData.contactPhone} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="+33 6 12 34 56 78" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <ChevronsRight className="w-5 h-5 mr-2 text-primary-600" />
              Type d'Intervention
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type d'intervention *</label>
                <input type="text" name="interventionType" value={formData.interventionType} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type IRVE</label>
                <input type="text" name="irveType" value={formData.irveType} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Choix de matériel ou service</label>
                <input type="text" name="materialOrService" value={formData.materialOrService} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                <select name="priority" value={formData.priority} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                  <option>Normale</option>
                  <option>Urgente</option>
                  <option>Haute</option>
                </select>
              </div>
            </div>
          </Card>

          <Card className="p-6">
             <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-primary-600" />
                Description de la demande
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titre de la demande *</label>
                    <input type="text" name="requestTitle" value={formData.requestTitle} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="Ex: Installation borne de recharge parking" />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description détaillée *</label>
                    <textarea name="detailedDescription" value={formData.detailedDescription} onChange={handleChange} required rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="Décrivez en détail les travaux à réaliser..."></textarea>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Budget estimé (€)</label>
                    <input type="number" name="estimatedBudget" value={formData.estimatedBudget} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="0" />
                </div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-primary-600" />
              Lieu et Planning
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse d'intervention *</label>
                <input type="text" name="interventionAddress" value={formData.interventionAddress} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="15 Rue de la Paix" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ville *</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="Paris" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Code Postal *</label>
                <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} required className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" placeholder="75001" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date préférée</label>
                <input type="date" name="preferredDate" value={formData.preferredDate} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
              </div>
            </div>
          </Card>

          <div className="flex justify-end space-x-4">
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              Annuler
            </Button>
            <Button type="submit" size="lg">
              <Send className="w-4 h-4 mr-2" />
              Envoyer
            </Button>
          </div>
        </form>
      </div>
    </PublicLayout>
  );
};

export default RequestQuote;
